import imageCompression from 'browser-image-compression';
import type { Options } from 'browser-image-compression';

import { useMSALPayload } from '@initia-digital/auth-client-utils';
import { useSnackbar } from '@initia-digital/component-library';
import { baseDefinedAndNotNullNarrowing } from '@initia-digital/ts-utils';

import { getS3Object } from '../api/objectStorage';


export const DEFAULT_APP_RESIZE_OPTIONS: Options = {
    maxSizeMB: 500 * 1024, // 500 KB
    maxWidthOrHeight: 1000
};

export const DEFAULT_WEB_RESIZE_OPTIONS = {
    maxSizeMB: 200 * 1024, // 200 KB
    maxWidthOrHeight: 700
};

/**
 * Scales an image file to fit within specified maximum dimensions while maintaining aspect ratio.
 * The scaling only occurs if the image exceeds either the maxWidth or maxHeight.
 * 
 * @param file - The original image File object to be scaled
 * @param maxWidth - Maximum width in pixels (default: 800)
 * @param maxHeight - Maximum height in pixels (default: 600)
 * @returns Promise resolving to a new File object containing the scaled image
 * @throws Error if canvas context cannot be obtained or blob creation fails
 */
export const scaleImage = async (file: File, imageResizeOptions?: Options): Promise<File> => {
    // ---------------------
    const setImageResizeOptions = imageResizeOptions ?? { preserveExif: true };

    try {
        const compressedFile = await imageCompression(file, setImageResizeOptions);   
        return compressedFile;
      } catch (error) {
        // ---------------------
        console.error(`Error scaling image: ${file.name}`);
        console.error(error);
        throw error;
      }
};




export const loadFileUrl = async <T extends { filename: string; id: string | null; name: string; path: string | null }>(image: T, authDetail: ReturnType<typeof useMSALPayload>, queueSnackbar?: ReturnType<typeof useSnackbar>['queueSnackbar']) => {
  // ---------------------
  const { id, path } = image;
  if (
      !baseDefinedAndNotNullNarrowing(path)
      || !baseDefinedAndNotNullNarrowing(id)
  ) throw new Error('File upload is missing id or path');

  const response = await getS3Object(authDetail, { path });
  // ---------------------
  if (response?.buffer) {
      try {
          const blob = new Blob([response.buffer], { type: response.mimeType ?? 'application/octet-stream' });
          return URL.createObjectURL(blob);
      } catch (error) {
          console.error('Error creating object URL:', error);
          queueSnackbar?.(`Error processing ${image.name}`, 'error', 'Error', 3000, '1rem');
      }
  }
  throw new Error('File upload is missing buffer');
}